// « À régler » (design 6, made compact after Ruben's review on 2 Nov 2026:
// « too wide, like a book »): at most four small cards on top of « Livreurs »,
// each a few words, one number and one step; the whole list in a narrow
// panel behind « Tout voir ». The week lives on the account page (AccountSide).
import { useState } from 'react';
import { S } from '../../lib/strings';
import { firstToCheck, todo, type TodoItem } from '../../lib/agences';
import { telHref } from '../../lib/phone';
import type { Agences } from '../../lib/types';
import { Ic, type IconName } from '../Icon';

const ICON: Record<TodoItem['icon'], IconName> = { wallet: 'wallet', clock: 'clock', alert: 'alert', doc: 'doc', users: 'users', lock: 'lock' };

interface Acts { onRider(id: string): void; onAccount(id: string): void; dispatchers: Record<string, string | null> }

/** The one step a point leads to: the account for money, the rider's file for a rider, the first file to check for papers. */
function useGo(data: Agences, { onRider, onAccount }: Acts) {
  return (t: TodoItem) => {
    if (t.acts.includes('check')) { const r = firstToCheck(data); if (r) onRider(r.id); return; }
    if (t.riderId) { onRider(t.riderId); return; }
    if (t.agencyId) onAccount(t.agencyId);
  };
}

function Card({ t, go, phone }: { t: TodoItem; go(t: TodoItem): void; phone: string | null }) {
  return (
    <div className={'card ag-ac ' + t.tone}>
      <button className="ag-acb" onClick={() => go(t)} aria-label={`${t.head} · ${t.name}`}>
        <span className="ag-acn"><span className="ag-ic"><Ic n={ICON[t.icon]} s="xs" /></span><span className="ag-acw">{t.name}</span></span>
        <b className="ag-ach">{t.head}</b>
        {t.figure && <span className="ag-acf num">{t.figure}</span>}
        <span className="ag-acs">{t.sub}</span>
      </button>
      {t.acts.includes('call') && phone && <a className="ibtn ag-acc2" href={telHref(phone)} aria-label={S.ag.t.call} title={S.ag.t.call}><Ic n="phone" s="xs" /></a>}
    </div>
  );
}

function Panel({ items, go, onClose }: { items: TodoItem[]; go(t: TodoItem): void; onClose(): void }) {
  const groups = (['today', 'week', 'mygaz'] as const).map((g) => ({ g, xs: items.filter((t) => t.group === g) })).filter((x) => x.xs.length);
  return (
    <>
      <div className="scrim on" onClick={onClose} />
      <aside className="drawer on ag-panel" role="dialog" aria-modal="true" aria-label={S.ag.att.title}>
        <div className="ag-eh"><h2>{S.ag.att.title}</h2><button className="ibtn" onClick={onClose} aria-label={S.ag.att.close}><Ic n="x" s="s" /></button></div>
        {groups.map(({ g, xs }) => (
          <section key={g}>
            <div className="ag-tg"><h3 className="cap">{S.ag.groups[g]}</h3><small className="muted">{S.ag.points(xs.length)}</small></div>
            {xs.map((t) => (
              <button key={t.id} className={'ag-pr ' + t.tone} onClick={() => { onClose(); go(t); }}>
                <span className="ag-ic"><Ic n={ICON[t.icon]} s="xs" /></span>
                <span className="ag-prt"><b>{t.head}</b><span className="muted">{t.name} · {t.sub}</span></span>
                {t.figure && <span className="ag-prf num">{t.figure}</span>}
                <Ic n="chev" s="xs" />
              </button>
            ))}
          </section>
        ))}
      </aside>
    </>
  );
}

export function Attention({ data, ...acts }: { data: Agences } & Acts) {
  const items = todo(data);
  const [open, setOpen] = useState(false);
  const go = useGo(data, acts);
  if (!items.length) {
    return <div className="ag-calm"><span className="ag-chk ok"><Ic n="check" s="xs" /></span><b>{S.ag.att.calm}</b><span className="muted">{S.ag.att.calmSub}</span></div>;
  }
  return (
    <section className="ag-att" aria-label={S.ag.att.title}>
      <div className="ag-atth">
        <h2>{S.ag.att.title} <span className="muted">{items.length}</span></h2>
        {items.length > 4 && <button className="link" onClick={() => setOpen(true)}>{S.ag.att.all(items.length)}<Ic n="chev" s="xs" /></button>}
      </div>
      <div className="ag-attg">
        {items.slice(0, 4).map((t) => <Card key={t.id} t={t} go={go} phone={t.agencyId ? acts.dispatchers[t.agencyId] : null} />)}
      </div>
      {open && <Panel items={items} go={go} onClose={() => setOpen(false)} />}
    </section>
  );
}
